<!--
  DuplicateArtworkNotice -- "we already have this file" (Bug Intake #310).

  Shown by an upload surface when the file it just uploaded is byte-identical
  to artwork the caller's business already owns (api: GET
  /api/artworks/duplicates, matched by content hash within the SAME
  distributor only). The upload is held, not dropped: the user chooses.

    - "Use <name>"     -> 'use-existing' with that artwork. No second copy.
    - "Upload anyway"  -> 'upload-anyway'. The user deliberately wants a
                          separate copy (a fork they will change).

  Presentational only. The host owns what each choice does, because a
  library page opens the artwork, a picker selects it, and a placement
  wizard binds it.
-->
<template>
    <div class="dan" role="alert" data-testid="duplicate-artwork-notice">
        <p class="dan__lede">
            <strong>This file is already in your library.</strong>
            Use the existing artwork instead of making a second copy.
        </p>
        <ul class="dan__list">
            <li
                v-for="d in duplicates"
                :key="d.id"
                class="dan__row"
                :data-testid="`duplicate-artwork-row-${d.id}`"
            >
                <span class="dan__name">{{ d.name }}</span>
                <span class="dan__meta">
                    {{ stateLabel(d.lifecycle_state) }} &middot;
                    {{ usedBy(d.used_by_count) }}
                </span>
                <button
                    v-if="isUsable(d)"
                    type="button"
                    class="dan__btn dan__btn--primary"
                    :disabled="busy"
                    :data-testid="`duplicate-artwork-use-${d.id}`"
                    @click="$emit('use-existing', d)"
                >
                    Use this one
                </button>
            </li>
        </ul>
        <button
            type="button"
            class="dan__btn"
            :disabled="busy"
            data-testid="duplicate-artwork-upload-anyway"
            @click="$emit('upload-anyway')"
        >
            Upload as a new copy anyway
        </button>
    </div>
</template>

<script setup>
defineProps({
    // GET /api/artworks/duplicates rows: {id, name, lifecycle_state,
    // used_by_count}.
    duplicates: { type: Array, required: true },
    busy: { type: Boolean, default: false },
})
defineEmits(['use-existing', 'upload-anyway'])

const STATE_LABELS = {
    draft: 'Draft',
    in_review: 'In review',
    approved: 'Approved',
    rejected: 'Rejected',
    archived: 'Archived',
}

// Defense in depth: the API already omits these; an archived or rejected
// artwork must never be offered for reuse.
const UNUSABLE = ['archived', 'rejected']

function isUsable(d) {
    return !UNUSABLE.includes(d.lifecycle_state)
}

function stateLabel(state) {
    return STATE_LABELS[state] || 'In your library'
}

function usedBy(count) {
    const n = Number(count) || 0
    if (n === 0) return 'not used on a product yet'
    return `used by ${n} ${n === 1 ? 'product' : 'products'}`
}
</script>

<style scoped>
.dan {
    border: 1px solid rgba(255, 152, 0, 0.5);
    background: rgba(255, 193, 7, 0.1);
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
}
.dan__lede {
    margin: 0;
}
.dan__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.dan__row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.dan__name {
    font-weight: 600;
}
.dan__meta {
    color: rgba(0, 0, 0, 0.6);
    flex: 1 1 auto;
}
.dan__btn {
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    align-self: flex-start;
}
.dan__btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}
.dan__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
